/**
 * This file is meant to represent some additional geometry calculation utils.
 * We should not really aim to have this be a big library of tooling, and rather we would want to experdite this stuff to
 * other libraries such as turf.
 */

import { LngLat, LngLatBounds } from 'mapbox-gl';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { polygon } from '@turf/helpers';

/**
 * Given a set of lat long points, this function will return a clostest fitting extent boundary around the set of points.
 * Note that it currently adds a hardcoded padding of 0.0001 degree (in lon & lat)
 * @param  {LngLat[]} pointset
 * @returns mapboxgl
 */
export const calculateExtentFromPointSet = (
  pointset: LngLat[]
): LngLatBounds | null => {
  const EXTENT_PADDING = 0.0001; // lat and long

  if (pointset.length === 0) {
    return null;
  }

  const lats = pointset.map(coords => coords.lat);
  const longs = pointset.map(coords => coords.lng);

  const minLat = Math.min(...lats) - EXTENT_PADDING;
  const maxLat = Math.max(...lats) + EXTENT_PADDING;

  const minLong = Math.min(...longs) - EXTENT_PADDING;
  const maxLong = Math.max(...longs) + EXTENT_PADDING;

  const bottomLeft = new LngLat(minLong, minLat);
  const topRight = new LngLat(maxLong, maxLat);

  return new LngLatBounds(bottomLeft, topRight);
};

/**
 * Given a coordinate, and a bounding box, this function will return whether the point is indide or outside the bounding box.
 * @param  {number[]} coordinate
 * @param  {LngLatBounds} bounds
 * @returns boolean
 */
export const isPointInBounds = (
  coordinate: number[],
  bounds: LngLatBounds
): boolean => {
  const boundsGeometry = polygon([
    [
      [bounds.getNorthWest().lng, bounds.getNorthWest().lat],
      [bounds.getNorthEast().lng, bounds.getNorthEast().lat],
      [bounds.getSouthEast().lng, bounds.getSouthEast().lat],
      [bounds.getSouthWest().lng, bounds.getSouthWest().lat],
      [bounds.getNorthWest().lng, bounds.getNorthWest().lat],
    ],
  ]);

  return booleanPointInPolygon(coordinate, boundsGeometry);
};
